import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import hassibiSignature from '../../assets/signature/hassibisignature.png';
import { auth } from '../../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingWhatsAppPDF, setIsGeneratingWhatsAppPDF] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [clientSignature, setClientSignature] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const pdfRef = useRef(null);
  const contentRef = useRef(null); // For content without buttons
  const signatureCanvasRef = useRef(null);

  // Get current authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const amount = searchParams.get('amount');
    const downPayment = searchParams.get('downPayment');
    const currency = searchParams.get('currency');
    const investorName = searchParams.get('investorName');
    const investorType = searchParams.get('investorType');
    const equity = searchParams.get('equity');
    const returns = searchParams.get('returns');
    const fullInvestmentAmount = searchParams.get('amount'); // This is the full amount
    const downPaymentAmount = searchParams.get('downPayment'); // This is what they paid (0.25%)
    
    // Only require essential payment info (amount, downPayment, currency, investorType)
    if (amount && downPayment && currency && investorType) {
      // Calculate remaining amount
      const remainingAmount = parseInt(fullInvestmentAmount) - parseInt(downPaymentAmount);
      
      setPaymentDetails({
        fullInvestmentAmount: parseInt(fullInvestmentAmount),
        downPaymentAmount: parseInt(downPaymentAmount),
        remainingAmount: remainingAmount,
        currency: currency,
        investorName: investorName ? decodeURIComponent(investorName) : 'Investor', // Default name if not provided
        investorType: decodeURIComponent(investorType),
        equity: equity ? parseFloat(equity) : 0.3, // Default to 0.3% for $500k investment
        returns: returns ? parseFloat(returns) : 1.5, // Default to 1.5% for $500k investment
        planTitle: `${fullInvestmentAmount} ${currency} Investment`
      });
    }
  }, [searchParams]);

  const handleGoToInvestment = () => {
    navigate('/investment');
  };

  const handleSignatureClick = () => {
    setShowUploadModal(true);
  };


  const handleSignNow = () => {
    setShowUploadModal(false);
    setShowSignatureModal(true);
    // If there's already a signature, load it into the canvas
    if (clientSignature && signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = clientSignature;
    }
  };


  const handleSignatureClose = () => {
    setShowSignatureModal(false);
    // Don't clear the canvas when closing, keep the signature for re-editing
  };

  const handleModalOverlayClick = (e) => {
    // Close modal when clicking outside the modal content
    if (e.target === e.currentTarget) {
      handleSignatureClose();
    }
  };

  const handleSignatureConfirm = () => {
    if (signatureCanvasRef.current) {
      const signatureData = signatureCanvasRef.current.toDataURL();
      setClientSignature(signatureData);
      setShowSignatureModal(false);
    }
  };

  const handleSignatureClear = () => {
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create a canvas to resize and process the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas size (similar to signature canvas)
          canvas.width = 400;
          canvas.height = 200;
          
          // Draw the uploaded image to the canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to data URL
          const signatureData = canvas.toDataURL('image/png');
          setClientSignature(signatureData);
          setShowUploadModal(false);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file.');
    }
  };

  // Signature drawing functionality
  useEffect(() => {
    if (showSignatureModal && signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      let isDrawing = false;
      let lastX = 0;
      let lastY = 0;
      
      const startDrawing = (e) => {
        isDrawing = true;
        const rect = canvas.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
      };
      
      const draw = (e) => {
        if (!isDrawing) return;
        
        const rect = canvas.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        ctx.beginPath();
        ctx.moveTo(lastX, lastY);
        ctx.lineTo(currentX, currentY);
        ctx.stroke();
        
        lastX = currentX;
        lastY = currentY;
      };
      
      const stopDrawing = () => {
        isDrawing = false;
      };
      
      canvas.addEventListener('mousedown', startDrawing);
      canvas.addEventListener('mousemove', draw);
      canvas.addEventListener('mouseup', stopDrawing);
      canvas.addEventListener('mouseout', stopDrawing);
      
      // Touch events for mobile
      canvas.addEventListener('touchstart', (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        canvas.dispatchEvent(mouseEvent);
      });
      
      canvas.addEventListener('touchmove', (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousemove', {
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        canvas.dispatchEvent(mouseEvent);
      });
      
      canvas.addEventListener('touchend', (e) => {
        e.preventDefault();
        const mouseEvent = new MouseEvent('mouseup', {});
        canvas.dispatchEvent(mouseEvent);
      });
      
      return () => {
        canvas.removeEventListener('mousedown', startDrawing);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', stopDrawing);
        canvas.removeEventListener('mouseout', stopDrawing);
      };
    }
  }, [showSignatureModal]);

  const handleWhatsAppShare = async () => {
    if (!contentRef.current) return;
    
    setIsGeneratingWhatsAppPDF(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 1.5, // Reduced scale to fit better
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        padding: 20, // Reduced padding
        height: 800, // Limit height to fit single page
        scrollX: 0,
        scrollY: 0
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add margins to the PDF
      const margin = 10; // Reduced margins
      const imgWidth = 210 - (margin * 2);
      const pageHeight = 297 - (margin * 2); // Full A4 height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Scale down if content is too tall for single page
      let finalImgHeight = imgHeight;
      if (imgHeight > pageHeight) {
        const scaleFactor = pageHeight / imgHeight;
        finalImgHeight = pageHeight;
        const finalImgWidth = imgWidth * scaleFactor;
        const xOffset = (210 - finalImgWidth) / 2; // Center horizontally
        pdf.addImage(imgData, 'PNG', xOffset, margin, finalImgWidth, finalImgHeight);
      } else {
        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, finalImgHeight);
      }
      
      const fileName = `Payment_Receipt_${paymentDetails.planTitle}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      // Open WhatsApp with the PDF ready to share
      const whatsappMessage = `🎉 Payment Successful! 🎉\n\nI've just completed my investment in ${paymentDetails.planTitle}!\n\nInvestment Details:\n• Amount: ${paymentDetails.currency} ${paymentDetails.fullInvestmentAmount.toLocaleString()}\n• Investor Type: ${paymentDetails.investorType}\n• ${paymentDetails.investorType === "Normal Investor" ? "Expected Return" : "Equity Share"}: ${paymentDetails.investorType === "Normal Investor" ? `${paymentDetails.returns}%` : `${paymentDetails.equity}%`}\n• Amount Remaining: ${paymentDetails.currency} ${paymentDetails.remainingAmount.toLocaleString()}\n\nCheck out the receipt PDF I've attached! 📄`;
      
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');
      
    } catch (error) {
      console.error('Error generating WhatsApp PDF:', error);
      alert('Failed to generate PDF for WhatsApp. Please try again.');
    } finally {
      setIsGeneratingWhatsAppPDF(false);
    }
  };

  const generatePDF = async () => {
    if (!contentRef.current) return;
    
    setIsGeneratingPDF(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 1.5, // Reduced scale to fit better
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        padding: 20, // Reduced padding
        height: 800, // Limit height to fit single page
        scrollX: 0,
        scrollY: 0
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add margins to the PDF
      const margin = 10; // Reduced margins
      const imgWidth = 210 - (margin * 2);
      const pageHeight = 297 - (margin * 2); // Full A4 height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Scale down if content is too tall for single page
      let finalImgHeight = imgHeight;
      if (imgHeight > pageHeight) {
        const scaleFactor = pageHeight / imgHeight;
        finalImgHeight = pageHeight;
        const finalImgWidth = imgWidth * scaleFactor;
        const xOffset = (210 - finalImgWidth) / 2; // Center horizontally
        pdf.addImage(imgData, 'PNG', xOffset, margin, finalImgWidth, finalImgHeight);
      } else {
        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, finalImgHeight);
      }
      
      const fileName = `Payment_Receipt_${paymentDetails.planTitle}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!paymentDetails) {
    return (
      <div className="payment-success-page">
        <div className="payment-success-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="payment-success-container" ref={pdfRef}>
        {/* PDF Download Button - Outside content area */}
        <button 
          className={`pdf-download-btn ${!clientSignature ? 'disabled' : ''}`}
          onClick={generatePDF}
          disabled={isGeneratingPDF || !clientSignature}
          title={!clientSignature ? "Please complete your signature first" : "Download Receipt as PDF"}
        >
          {isGeneratingPDF ? (
            <span className="pdf-loading">⏳</span>
          ) : (
            <span className="pdf-icon">📄</span>
          )}
        </button>
        
        {/* Content for PDF generation (without buttons) */}
        <div ref={contentRef} className="pdf-content">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
          </div>
          
          <h1 className="payment-success-title">Payment Successful!</h1>
          <p className="success-subtitle">
            Your down payment has been processed successfully. Welcome to the {paymentDetails.planTitle}!
          </p>

          <div className="payment-summary">
            <div className="summary-item">
              <span className="summary-label">Down Payment Paid:</span>
              <span className="summary-value">{paymentDetails.currency} {paymentDetails.downPaymentAmount.toLocaleString()}</span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Full Investment Amount:</span>
              <span className="summary-value">{paymentDetails.currency} {paymentDetails.fullInvestmentAmount.toLocaleString()}</span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Investor Type:</span>
              <span className="summary-value investor-type-value">
                {paymentDetails.investorType}
              </span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">
                {paymentDetails.investorType === "Normal Investor" ? "Expected Return:" : "Equity Share:"}
              </span>
              <span className="summary-value ">
                {paymentDetails.investorType === "Normal Investor" 
                  ? `${paymentDetails.returns}%` 
                  : `${paymentDetails.equity}%`
                }
              </span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Amount Remaining:</span>
              <span className="summary-value remaining-value">
                {paymentDetails.currency} {paymentDetails.remainingAmount.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="success-message">
            <p>
              Thank you for your down payment. Your investment is now active and will be managed by our expert team.
            </p>
          </div>

          {/* Signature Section */}
          <div className="signature-section">
            {!clientSignature ? (
              <div className="signature-required-notice">
                <span className="notice-icon">⚠️</span>
                <span>Please complete your signature below to proceed</span>
              </div>
            ) : (
              <div className="signature-completed-notice">
                <span className="notice-icon">✅</span>
                <span>Signature completed! You can now proceed with your actions.</span>
              </div>
            )}
            <div className="signature-container">
              {/* Company Signature */}
              <div className="company-signature">
                <div className="company-name-box">
                  <div className="company-name-label">Company Name:</div>
                  <div className="company-name-value">iWinto LLC</div>
                </div>
                <div className="signature-label">Company Signature</div>
                <div className="signature-image-container">
                  <img 
                    src={hassibiSignature} 
                    alt="Company Signature" 
                    className="company-signature-image"
                  />
                </div>
              </div>

              {/* Client Signature */}
              <div className="client-signature">
                <div className="client-name-box">
                  <div className="client-name-label">Client Name:</div>
                  <div className="client-name-value">
                    {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Client'}
                  </div>
                </div>
                <div className="signature-label">Client Signature</div>
                <div className="signature-placeholder">
                  {clientSignature ? (
                    <div 
                      className="signature-image-wrapper clickable-signature"
                      onClick={handleSignatureClick}
                      title="Click to change signature"
                    >
                      <img 
                        src={clientSignature} 
                        alt="Client Signature" 
                        className="client-signature-image"
                      />
                    </div>
                  ) : (
                    <button 
                      className="signature-button"
                      onClick={handleSignatureClick}
                    >
                      <span className="pen-icon">✏️</span>
                      <span>Click to Sign</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons - Outside PDF content */}
        <div className="payment-success-actions">
          <button 
            className={`btn-whatsapp ${!clientSignature ? 'disabled' : ''}`}
            onClick={handleWhatsAppShare}
            disabled={isGeneratingWhatsAppPDF || !clientSignature}
            title={!clientSignature ? "Please complete your signature first" : ""}
          >
            {isGeneratingWhatsAppPDF ? (
              <span className="whatsapp-loading">⏳</span>
            ) : (
              <span className="whatsapp-icon"></span>
            )}
            Share on WhatsApp
          </button>
          <button 
            className={`btn-primary ${!clientSignature ? 'disabled' : ''}`}
            onClick={handleGoToInvestment}
            disabled={!clientSignature}
            title={!clientSignature ? "Please complete your signature first" : ""}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Upload Options Modal */}
      {showUploadModal && (
        <div className="signature-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowUploadModal(false)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signature-modal-header">
              <h3>Choose Signature Method</h3>
              <button 
                className="close-button"
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="upload-modal-content">
              <div className="upload-options">
                <div className="upload-option">
                  <div className="upload-icon">📁</div>
                  <h4>Upload Signature</h4>
                  <p>Upload an image of your signature</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="signature-upload"
                  />
                  <button 
                    className="btn-upload"
                    onClick={() => document.getElementById('signature-upload').click()}
                  >
                    Choose File
                  </button>
                </div>
                
                <div className="upload-option">
                  <div className="upload-icon">✏️</div>
                  <h4>Sign Now</h4>
                  <p>Draw your signature using mouse or touch</p>
                  <button 
                    className="btn-sign"
                    onClick={handleSignNow}
                  >
                    Start Drawing
                  </button>
                </div>
              </div>
              
              {clientSignature && (
                <div className="delete-signature-section">
                  <div className="delete-signature-option">
                    <div className="upload-icon">🗑️</div>
                    <h4>Delete Signature</h4>
                    <p>Remove your current signature</p>
                    <button 
                      className="btn-delete"
                      onClick={() => {
                        setClientSignature('');
                        setShowUploadModal(false);
                      }}
                    >
                      Delete Signature
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="signature-modal-overlay" onClick={handleModalOverlayClick}>
          <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signature-modal-header">
              <h3>Draw Your Signature</h3>
              <button 
                className="close-button"
                onClick={handleSignatureClose}
              >
                ×
              </button>
            </div>
            
            <div className="signature-modal-content">
              <div className="signature-canvas-container">
                <canvas
                  ref={signatureCanvasRef}
                  className="signature-canvas"
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'crosshair'
                  }}
                />
              </div>
              
              <div className="signature-modal-actions">
                <button 
                  className="btn-clear"
                  onClick={handleSignatureClear}
                >
                  Clear
                </button>
                <button 
                  className="btn-confirm"
                  onClick={handleSignatureConfirm}
                >
                  Confirm Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
